import { makeAutoObservable, reaction } from 'mobx'
import { characters, resources } from '../utils/sets'
import { numFormat, addToObj } from '../utils/helpers'
import { HarvestStore, createHarvestStore } from './harvestStore'

export interface ProfileStore {
  // Usuario
  username: string
  gender: string
  flag: string
  soundsOn: boolean
  
  // Stats
  score: number
  level: number
  scoreForm: string
  
  // Recursos
  resources: Record<string, number>
  
  // Juegos
  currHarvest: HarvestStore
  
  // Métodos
  setName: (name: string) => void
  setGender: (gender: string) => void
  setFlag: (flag: string) => void
  toggleSounds: () => void
  addScore: (amount: number) => void
  addResource: (icon: string, amount: number) => void
  reset: () => void
  loadFromStorage: () => void
  saveToStorage: () => void
  emoji: string
  volume: string
  displayName: string
}

export const DEFAULT_FLAG = '🇪🇸'

// Genera un nombre único tipo "Player_A3X9"
const generatePlayerName = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  const suffix = Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  return `Player_${suffix}`
}

// Cargar datos del localStorage
const loadStoredProfile = () => {
  try {
    const stored = localStorage.getItem('planetmoji_profile')
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.warn('Failed to load profile from storage')
  }
  return null
}

const createProfileStore = (): ProfileStore => {
  // Intentar cargar perfil guardado
  const stored = loadStoredProfile()
  
  const store = makeAutoObservable({
    // Usuario - cargar de storage o generar nuevo
    username: stored?.username || generatePlayerName(),
    gender: stored?.gender || Object.keys(characters)[0],
    flag: stored?.flag || DEFAULT_FLAG,
    soundsOn: stored?.soundsOn ?? true,

    // Stats
    score: stored?.score || 1,

    // Recursos iniciales
    resources: stored?.resources || {
      '💎': 150,
      '🪵': 150,
      '🥩': 150,
      '🪨': 150,
    } as Record<string, number>,

    // Juego actual
    currHarvest: createHarvestStore(),

    // Computed
    get level(): number {
      return Math.floor(Math.log10(this.score)) + 1
    },

    get scoreForm(): string {
      return numFormat(this.score)
    },

    get emoji(): string {
      const genderEmojis = characters[this.gender]
      return genderEmojis?.[Math.min(this.level - 1, genderEmojis.length - 1)] || '👤'
    },

    get volume(): string {
      return this.soundsOn ? '0.85' : '0'
    },

    get displayName(): string {
      return this.username || generatePlayerName()
    },

    // Acciones
    setName(name: string) {
      this.username = name
      this.saveToStorage()
    },

    setGender(gender: string) {
      this.gender = gender
      this.saveToStorage()
    },

    setFlag(flag: string) {
      this.flag = flag
      this.saveToStorage()
    },

    toggleSounds() {
      this.soundsOn = !this.soundsOn
      this.saveToStorage()
    },

    addScore(amount: number) {
      this.score += amount
      this.saveToStorage()
    },

    addResource(icon: string, amount: number) {
      if (this.resources[icon] !== undefined) {
        this.resources[icon] += amount
        this.saveToStorage()
      }
    },

    reset() {
      this.score = 1
      this.flag = DEFAULT_FLAG
      this.gender = Object.keys(characters)[0]
      this.username = generatePlayerName()
      this.resources = {
        '💎': 150,
        '🪵': 150,
        '🥩': 150,
        '🪨': 150,
      }
      this.saveToStorage()
    },

    saveToStorage() {
      try {
        const data = {
          username: this.username,
          gender: this.gender,
          flag: this.flag,
          soundsOn: this.soundsOn,
          score: this.score,
          resources: this.resources,
        }
        localStorage.setItem('planetmoji_profile', JSON.stringify(data))
      } catch (e) {
        console.warn('Failed to save profile to storage')
      }
    },

    loadFromStorage() {
      const stored = loadStoredProfile()
      if (stored) {
        this.username = stored.username || this.username
        this.gender = stored.gender || this.gender
        this.flag = stored.flag || this.flag
        this.soundsOn = stored.soundsOn ?? this.soundsOn
        this.score = stored.score || this.score
        this.resources = stored.resources || this.resources
      }
    },
  })

  // Guardar automáticamente al iniciar
  store.saveToStorage()

  return store
}

// Singleton del profile store
export const profileStore = createProfileStore()
